import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import api from '../lib/api';

function pad(n) { return String(n).padStart(3, '0'); }

function NoteBody({ text }) {
  const paras = text.split(/\n\n+/);
  return (
    <div className="space-y-6 md:space-y-8">
      {paras.map((p, i) => {
        const trimmed = p.trim();
        if (!trimmed) return null;
        // Bulleted lines starting with —
        if (/^—\s/.test(trimmed)) {
          return (
            <p key={i} className="mono text-[15px] md:text-[17px] text-ink/85 leading-[1.85] pl-6 border-l-2 border-orchid/60">
              {trimmed.replace(/^—\s/, '')}
            </p>
          );
        }
        return (
          <p key={i} className="mono text-[15px] md:text-[17px] text-ink/85 leading-[1.85]">
            {trimmed}
          </p>
        );
      })}
    </div>
  );
}

export default function FieldNotes() {
  const [notes, setNotes] = useState([]);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/field-notes')
      .then((r) => setNotes(r.data))
      .catch((e) => setErr('Notes unavailable'));
  }, []);

  return (
    <section id="field-notes" className="relative py-28 md:py-40 bg-gradient-to-b from-paper to-lilac/40" data-testid="field-notes-section">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <div className="flex items-baseline justify-between mb-14">
          <div className="mono text-xs uppercase tracking-[0.3em] text-orchid">(03) — Field notes</div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-ink/40 hidden md:block">Numbered · Dated · Owned</div>
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.9 }}
          className="serif text-[12vw] md:text-[8vw] leading-[0.9] text-ink mb-16 md:mb-24"
        >
          Notes from the <span className="italic text-grape">operator</span>.
        </motion.h2>

        {err && <p data-testid="notes-error" className="mono text-sm text-red-800/70">{err}</p>}

        <div className="space-y-32 md:space-y-48">
          {notes.map((n, idx) => (
            <motion.article
              key={n.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16"
              data-testid={`field-note-${n.number}`}
            >
              {/* huge numeral */}
              <div className="lg:col-span-3 relative">
                <div className="serif text-[28vw] md:text-[16vw] lg:text-[12vw] leading-[0.8] text-orchid/25 select-none">
                  {pad(n.number)}
                </div>
                <div className="mono text-[10px] uppercase tracking-[0.3em] text-ink/60 mt-2">
                  {new Date(n.created_at).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
                </div>
              </div>

              <div className="lg:col-span-9 lg:pt-6">
                <div className="mono text-[10px] uppercase tracking-[0.3em] text-royal mb-4">
                  Entry {pad(n.number)} · {n.subtitle || 'Eileen Tadeo · Berlin'}
                </div>
                <h3 className="serif text-3xl md:text-5xl lg:text-6xl text-ink leading-[1.02] tracking-tight mb-10">
                  {n.title}
                </h3>
                <NoteBody text={n.body} />

                {n.number === 1 && (
                  <div className="mt-14 pl-6 md:pl-10 border-l-4 border-orchid">
                    <p className="serif italic text-2xl md:text-4xl text-grape leading-tight">
                      &ldquo;Curiosity is still the human superpower. A bot can generate. A human can wonder.&rdquo;
                    </p>
                  </div>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
